import React from 'react';
import clsx from 'clsx';
import { ExclamationTriangleIcon, NoSymbolIcon } from '@heroicons/react/16/solid';
import { ReservationRuleViolation } from '../clients/rust-api-client';
import { ruleLabel } from './reservationMeta';
import { checkboxClass, inputClass } from './formStyles';

/**
 * The rules a booking breaks, as the manager decides about each one.
 *
 * Overrides are **per rule code, never a blanket "force"**: each
 * overridable violation gets its own checkbox, and one reason covers the
 * ones ticked. The reason goes on the reservation's audit trail with the
 * codes, so "why did 12 Elm get the clubhouse three weekends running" has
 * an answer. A rule that can never be overridden — the slot is taken, the
 * time is in the past — is shown without a checkbox, so the only way
 * forward is to change the booking.
 */
interface Props {
  violations: ReservationRuleViolation[];
  /** Hide the checkboxes (a read-only viewer, or a homeowner-facing preview). */
  readOnly?: boolean;
  ticked: string[];
  onTickedChange: (codes: string[]) => void;
  reason: string;
  onReasonChange: (reason: string) => void;
  /** Distinct per mount, for the reason field's id. */
  idPrefix: string;
}

export const RuleViolationsPanel: React.FC<Props> = ({
  violations,
  readOnly = false,
  ticked,
  onTickedChange,
  reason,
  onReasonChange,
  idPrefix,
}) => {
  if (violations.length === 0) return null;
  const hard = violations.filter((v) => !v.overridable);
  const anyTicked = ticked.length > 0;

  const toggle = (code: string, on: boolean) =>
    onTickedChange(on ? [...ticked, code] : ticked.filter((c) => c !== code));

  return (
    <section
      aria-label="Rules this booking breaks"
      className="rounded-lg border border-warning/40 bg-warning/5 px-3 py-2.5"
    >
      <p className="mb-2 flex items-center gap-1.5 text-[13px] font-medium text-fg">
        <ExclamationTriangleIcon className="h-4 w-4 text-warning" aria-hidden />
        {violations.length === 1
          ? 'This booking breaks a rule'
          : `This booking breaks ${violations.length} rules`}
      </p>
      <ul className="space-y-1.5">
        {violations.map((v) => (
          <li key={v.code} className="flex items-start gap-2 text-[13px]">
            {v.overridable && !readOnly ? (
              <input
                id={`${idPrefix}-${v.code}`}
                type="checkbox"
                className={clsx(checkboxClass, 'mt-0.5')}
                checked={ticked.includes(v.code)}
                onChange={(e) => toggle(v.code, e.target.checked)}
              />
            ) : (
              <NoSymbolIcon
                className={clsx('mt-0.5 h-4 w-4 shrink-0', v.overridable ? 'text-fg-muted' : 'text-danger')}
                aria-hidden
              />
            )}
            <label htmlFor={`${idPrefix}-${v.code}`} className="min-w-0">
              <span className="font-medium text-fg-secondary">{ruleLabel(v.code)}</span>
              <span className="text-fg-muted"> — {v.message}</span>
              {!v.overridable && (
                <span className="block text-[12px] text-danger">
                  Cannot be overridden — change the booking.
                </span>
              )}
            </label>
          </li>
        ))}
      </ul>
      {!readOnly && hard.length < violations.length && (
        <label
          htmlFor={`${idPrefix}-reason`}
          className="mt-3 flex flex-col gap-1 text-[12px] font-medium text-fg-muted"
        >
          Reason for overriding{anyTicked ? ' (required)' : ''}
          <textarea
            id={`${idPrefix}-reason`}
            rows={2}
            value={reason}
            onChange={(e) => onReasonChange(e.target.value)}
            placeholder="Recorded on the reservation's history with the rules overridden."
            className={clsx(inputClass, 'resize-none')}
          />
        </label>
      )}
    </section>
  );
};

export default RuleViolationsPanel;
